import { isLoading, fmap } from 'hooks/useAPI';
import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import html2canvas from 'html2canvas';
import { UseChartRenderer, useChartRenderer } from './useChartRenderer';
import { useModal } from 'hooks/useModal';
import { SNSShareButton } from './components/ShareButton';
import {
  SNSLinkageStatus,
  SNSLinkageType,
  useSNSLinkage,
  useSNSLinkageStatus,
} from './SNSLinkageList';
import { SNS } from './components/SNS';
import { ShareModal } from './components/ShareModal';
import { LinkConfirmModal } from './components/LinkConfirmModal';
import { SNSErrorModal } from './components/SNSErrorModal';
import { useRouting } from 'hooks/useRouting';
import { snslinkages, snspreview } from 'config/Routes';
import { writePreviewDataURL } from './ContinuePreview';
import { wordCount } from './wordCount';
import { initializeScrolTop } from 'utils/dom';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { TopixData } from 'components/features/home/assetStuffing/AssetStuffing';

export type StateRenderer<State, Extra> = (props: {
  state: State;
  setDataURL: React.Dispatch<React.SetStateAction<string | null>>;
  extra: Extra;
  topixExtra: TopixData[];
}) => React.ReactElement | null;

export type StateController<State> = (prop: {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
}) => React.ReactElement | null;

type PreviewProps<State, Extra> = (props: {
  setDataURL: React.Dispatch<React.SetStateAction<string | null>>;
  setComment: React.Dispatch<
    React.SetStateAction<
      { tag: 'invalid'; value: string } | { tag: 'valid'; value: string }
    >
  >;
  initialComment: string | null;
  StateRenderer: StateRenderer<State, Extra>;
  StateController: StateController<State>;
  defaultState: State;
  extra: Extra;
  topixExtra: TopixData[];
  onCancel: () => void;
}) => React.ReactElement | null;

const PreviewInternal = function PreviewInternal<State, Extra>({
  setDataURL,
  StateRenderer,
  StateController,
  defaultState,
  extra,
  topixExtra,
  comment,
  setComment,
}: Omit<
  ComponentProps<PreviewProps<State, Extra>>,
  'initialComment' | 'onCancel'
> & {
  comment: string;
}): ReturnType<PreviewProps<State, Extra>> {
  const [state, setState] = useState<State>(defaultState);
  const { CommentTextArea } = useCommentTextArea({ comment, setComment });
  return (
    <>
      <div className="shadow-lg">
        <StateRenderer {...{ state, setDataURL, extra, topixExtra }} />
      </div>
      <StateController {...{ state, setState }} />
      <div className="px-4">{CommentTextArea}</div>
    </>
  );
};

const CancelButton = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <>
      <button
        className="bg-white py-2 flex justify-center items-center text-gray-700"
        onClick={(): void => {
          initializeScrolTop();
          onCancel();
        }}>
        キャンセル
      </button>
    </>
  );
};

export const useCommentTextArea = ({
  setComment,
  comment,
}: {
  setComment: (
    comment: { tag: 'invalid'; value: string } | { tag: 'valid'; value: string }
  ) => void;
  comment: string;
}): { CommentTextArea: React.ReactElement } => {
  const maxLength = 140;
  const isCommentError = (comment: string): boolean =>
    wordCount(comment)(true) > maxLength;
  const CommentTextArea = (
    <>
      <textarea
        className={`mt-4 bg-white rounded-lg border p-3 text-base text-bluegray-700 w-full ${
          isCommentError(comment) ? 'border-tradewell' : 'border-bluegray-300'
        }`}
        style={isCommentError(comment) ? { backgroundColor: '#fee4e4' } : {}}
        rows={3}
        value={comment}
        onChange={e =>
          setComment(
            ((s): Parameters<typeof setComment>[0] =>
              isCommentError(s)
                ? { tag: 'invalid', value: s }
                : { tag: 'valid', value: s })(e.currentTarget.value)
          )
        }
      />
      <div
        className={`flex flex-col text-sm ${
          isCommentError(comment) ? 'text-tradewell' : 'text-bluegray-300'
        }`}>
        {isCommentError(comment) && (
          <>
            <div className="ml-auto mt-1 text-tradewell text-sm">
              {maxLength}文字以内で入力してください
            </div>
          </>
        )}
        <div className="ml-auto mt-1">
          {wordCount(comment)(true)}/{maxLength}
        </div>
      </div>
    </>
  );
  return { CommentTextArea };
};

export const Preview = <State, Extra>({
  StateController,
  StateRenderer,
  defaultState,
  extra,
  topixExtra,
  onCancel: onCancel_,
  modalShowedSNS,
  initialComment,
}: Omit<
  ComponentProps<PreviewProps<State, Extra>>,
  'setDataURL' | 'setComment'
> &
  Partial<{
    modalShowedSNS: SNS;
  }>): ReturnType<PreviewProps<State, Extra>> => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCancel = useCallback(onCancel_, []);
  const [dataURL, setDataURL] = useState<string | null>(null);
  const [comment, setComment] = useState<
    { tag: 'valid'; value: string } | { tag: 'invalid'; value: string }
  >({ tag: 'valid', value: initialComment ?? '#アセプラ ' });
  const twitterStatus = useSNSLinkageStatus('twitter', snspreview);
  const twitter = useSNSLinkage(
    'twitter',
    fmap(({ state }: { state: { status: SNSLinkageStatus } }) => ({
      status: state.status,
    }))(twitterStatus)
  );
  const twitterModal = useModal();
  const pushRouting = (({ pushRouting }) => (path: string) => () =>
    pushRouting({ path }, true))(useRouting());
  useEffect(() => {
    if (modalShowedSNS) {
      ((): (() => void) => {
        switch (modalShowedSNS) {
          case 'twitter':
            return twitterModal[1].showModal;
        }
      })()();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalShowedSNS]);
  const Modal = ({
    hide,
    isShow,
    snsLinkage,
    snsLinkageStatus,
    sns,
  }: {
    isShow: boolean;
    hide: () => void;
    snsLinkage: SNSLinkageType;
    snsLinkageStatus: SNSLinkageStatus;
    sns: SNS;
  }): React.ReactElement => {
    if (
      snsLinkage.tag === 'Certified' &&
      snsLinkageStatus.tag === 'Certified'
    ) {
      return (
        <ShareModal
          {...{
            hide,
            isShow,
            onCancel: hide,
            goBack: onCancel,
            icon: snsLinkageStatus.icon,
            name: snsLinkageStatus.name,
            onChangeSNSAccount: pushRouting(snslinkages),
            onShare: async () => {
              if (!dataURL) return { tag: 'NotFoundDataURL' };
              if (comment.tag === 'invalid') return { tag: 'Failure' };
              const ret = await snsLinkage.share(dataURL, comment.value);
              return ret.tag === 'Failure'
                ? { tag: 'Failure' }
                : { tag: 'Success' };
            },
          }}
        />
      );
    }
    if (
      snsLinkage.tag === 'Uncertified' &&
      snsLinkageStatus.tag === 'Uncertified'
    ) {
      return (
        <LinkConfirmModal
          {...{
            hide,
            isShow,
            onCancel: async () => {
              dataURL && writePreviewDataURL(dataURL, comment.value, sns);
              hide();
            },
            onClickSNSLink: async () => {
              dataURL && writePreviewDataURL(dataURL, comment.value, sns);
              snsLinkage.certificate();
            },
            sns,
          }}
        />
      );
    }
    if (snsLinkage.tag === 'Failed' || snsLinkageStatus.tag === 'Failed')
      return <SNSErrorModal hideModal={hide} isShowModal={isShow} />;
    return <></>;
  };
  return (
    <>
      <PreviewInternal
        {...{
          setDataURL,
          StateRenderer,
          StateController,
          defaultState,
          extra,
          topixExtra,
          onCancel,
          comment: comment.value,
          setComment,
        }}
      />
      <div className="flex flex-col gap-2 p-4">
        {(['twitter'] as SNS[]).map(sns => {
          return (
            <SNSShareButton
              {...{
                key: sns,
                disabled: !dataURL || comment.tag === 'invalid',
                sns,
                onClick: twitterModal[1].showModal,
              }}
            />
          );
        })}
        <CancelButton key="cancel" onCancel={onCancel} />
      </div>
      {!isLoading(twitter) && !isLoading(twitterStatus) && (
        <Modal
          {...{
            hide: twitterModal[1].hideModal,
            isShow: twitterModal[0],
            sns: 'twitter',
            snsLinkage: twitter.linkage,
            snsLinkageStatus: twitterStatus.state.status,
          }}
        />
      )}
    </>
  );
};

export type ChartPreviewProps<State, Extra> = {
  StateController: StateController<State>;
  Chart: Parameters<
    UseChartRenderer<Extra, State>['useChartRenderer']
  >[0]['Chart'];
  stateToOptions: Parameters<
    UseChartRenderer<Extra, State>['useChartRenderer']
  >[0]['stateToOptions'];
  RenderedChartToElement: Parameters<
    UseChartRenderer<Extra, State>['useChartRenderer']
  >[0]['RenderedChartToElement'];
  StateRenderer: StateRenderer<State, Extra>;
  defaultState: State;
  extra: Extra;
  topixExtra?: TopixData[];
  onCancel: () => void;
  chartTitle: string;
  initialComment?: string;
};

const ChartPreviewPage = <State, Extra>(
  props: ChartPreviewProps<State, Extra>
) => {
  if (!props) return <>不正なページです</>;
  return (
    <>
      <div className="pb-6">
        <div className="bg-white max-w-md mx-auto">
          <Preview
            {...{
              defaultState: props.defaultState,
              extra: props.extra,
              topixExtra:
                props.topixExtra !== undefined ? props.topixExtra : [],
              StateController: props.StateController,
              StateRenderer: props.StateRenderer,
              onCancel: props.onCancel,
              initialComment: props.initialComment ?? null,
            }}
          />
        </div>
      </div>
    </>
  );
};

export const width = 300;
export const height = 250;

export const elementToDataURL = async (
  elem: React.ReactElement
): Promise<string> => {
  const rendered = renderToStaticMarkup(elem);
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '10000px';
  wrapper.style.left = '10000px';
  wrapper.style.width = `${width}px`;
  document.body.appendChild(wrapper);
  wrapper.innerHTML = rendered;
  const canvas = await html2canvas(wrapper, {
    width,
    logging: false,
  });
  document.body.removeChild(wrapper);
  return canvas.toDataURL();
};

export type UsePreviewProps<State, Extra> = Omit<
  ChartPreviewProps<State, Extra>,
  'StateRenderer'
>;

export const usePreview = <State, Extra>(
  props: UsePreviewProps<State, Extra>
) => {
  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    setBack({ backFunction: props.onCancel });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isShowPreview] = useModal();
  const { StateRenderer, setRange } = useChartRenderer({
    Chart: props.Chart,
    RenderedChartToElement: props.RenderedChartToElement,
    stateToOptions: props.stateToOptions,
    chartTitle: props.chartTitle,
  });
  const PreviewButton = ({ className }: { className: string }) => <></>;
  const TwitterIconPreviewButton = ({ className }: { className: string }) => (
    <></>
  );

  const preview = ():
    | { tag: 'Render'; rendered: React.ReactElement }
    | { tag: 'NotRender' } => {
    return isShowPreview
      ? {
          tag: 'Render',
          rendered: <ChartPreviewPage {...{ ...props, StateRenderer }} />,
        }
      : { tag: 'NotRender' };
  };
  return { PreviewButton, TwitterIconPreviewButton, preview, setRange };
};
